import { useSelector } from 'react-redux';
import type { ThemeColor } from '../../../../component_library/styles/theme';
import { issuerSelector } from '../../../../selectors';
import { LoginMethod } from '../../../../selectors/interfaces';
import type { EmployeeLoginType } from '../../../../selectors/interfaces';
import { useGetSsoLoginLink } from '../../../../hooks/useGetSsoLoginLink';
import { useGetLoginConfig } from '../../../../hooks/useGetLoginConfig';
import { convertAbsoluteUrlToRelativeUrl } from '../../../../app/router-utils';
import { useGetTheme } from '../../../../hooks/use-get-theme';
import { AllowedInternalLoginMethods } from './use-login-dob-form';
import { useLoginNavigation } from './use-login-navigation';

interface IProps {
  businessGroupsSeparator?: string;
}

export interface ILoginProvider {
  loginMethod: LoginMethod;
  url: string;
  isExternalUrl: boolean;
  userTypes: ReadonlyArray<EmployeeLoginType>;
  name?: string;
  iconUrl?: string;
  providerColor?: ThemeColor;
}

const useLoginProviders = ({ businessGroupsSeparator }: IProps) => {
  const theme = useGetTheme();

  const { login } = useSelector(issuerSelector);
  const { getLoginMethodConfig, isExternalLoginMethod } = useGetLoginConfig();
  const { getSsoLinkForLoginMethod: getLinkToExternalLoginMethod } =
    useGetSsoLoginLink();
  const { getLinkToInternalLoginMethod } = useLoginNavigation();

  const externalLoginMethods =
    login?.supported_login_methods?.filter((loginMethod) =>
      isExternalLoginMethod(loginMethod)) ?? [];
  const internalLoginMethods =
    login?.supported_login_methods?.filter(
      (x) => externalLoginMethods.indexOf(x) < 0,
    ) ?? [];

  const externalLoginProviders: ILoginProvider[] = externalLoginMethods
    .map((externalLoginMethod) => ({
      externalLoginMethod,
      url: getLinkToExternalLoginMethod(
        externalLoginMethod,
        true,
        externalLoginMethod === LoginMethod.SSO,
      ),
    }))
    .filter(({ url }) => url)
    .map(({ externalLoginMethod, url }) => ({
      loginMethod: externalLoginMethod,
      url: url ?? '',
      userTypes: getLoginMethodConfig(externalLoginMethod)?.user_case ?? [],
      isExternalUrl: true,
      ...(externalLoginMethod === LoginMethod.SSO
        ? {
          name: login.sso_provider_name ?? undefined,
          iconUrl: login.sso_provider_logo ?? undefined,
          providerColor:
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              (login.sso_provider_color as ThemeColor) ?? undefined,
        }
        : {}),
    }));

  const internalLoginProviders: ILoginProvider[] = internalLoginMethods
    .filter((internalLoginMethod) =>
      AllowedInternalLoginMethods.includes(internalLoginMethod))
    .map((internalLoginMethod) => {
      const destinationUrl = getLinkToInternalLoginMethod(internalLoginMethod);
      return {
        loginMethod: internalLoginMethod,
        userTypes: getLoginMethodConfig(internalLoginMethod)?.user_case ?? [],
        url: convertAbsoluteUrlToRelativeUrl(destinationUrl.toString()),
        isExternalUrl: false,
        name:
          internalLoginMethod === LoginMethod.BUSINESS_GROUP &&
          businessGroupsSeparator
            ? getLoginMethodConfig(
              LoginMethod.BUSINESS_GROUP,
              false,
            )?.business_groups?.join(` ${businessGroupsSeparator} `)
            : undefined,
      };
    });

  const loginMethodsOrder = Object.values(LoginMethod);
  const loginProviders = [
    ...externalLoginProviders,
    ...internalLoginProviders,
  ].sort(
    (a, b) =>
      loginMethodsOrder.indexOf(a.loginMethod) -
      loginMethodsOrder.indexOf(b.loginMethod),
  );

  return {
    theme,
    loginProviders,
  };
};

export default useLoginProviders;
